import type { ProcessingLiveness } from '../services/api';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Play,
  CheckCircle,
  Clock,
  RefreshCw,
  AlertCircle,
  Trash2,
  MoreHorizontal
} from 'lucide-react';
import { MarkerInfo, PromptRunFailureOperationCounts, PromptRunFailedSegment, Video } from '../services/api';
import { getDisplayedVideoStatus, getRunScopedVideoProjection, getVideoProgressSummary } from '../utils/runScopedVideoStatus';
import ProcessingProgressBar from './ProcessingProgressBar';
import ImageEnrichmentStatus from './ImageEnrichmentStatus';
import { isInProgressStatus } from '../utils/realtimeStreamEligibility';
import { formatMediaDuration } from '../utils/mediaDuration';
import MarkerEditor from './MarkerEditor';
import GrantedPreviewImage from './GrantedPreviewImage';
import VideoSegmentStatusBars, { getVideoSegmentStatusRows } from './VideoSegmentStatusBars';
import type { PreviewGrantProjection } from '../contexts/SearchWorkspaceContext';
import './VideoTile.css';

interface VideoTileProps {
  video: Video;
  onTileClick: (video: Video) => void;
  onProcess?: (videoId: string) => void;
  onDelete: (videoId: string) => void;
  isProcessing: boolean;
  isReadOnly?: boolean;
  isInteractive?: boolean;
  isSelected?: boolean;
  selectionMode?: boolean;
  infoMode?: 'hover' | 'compact';
  onSelectionChange?: (videoId: string, selected: boolean) => void;
  isActiveInSidebar?: boolean;
  showMarker?: boolean;
  showMetadataKeys?: boolean;
  showStatusText?: boolean;
  showTopStatusBar?: boolean;
  showSegmentStatusBarsAlways?: boolean;
  showSegmentStatusBars?: boolean;
  runScopeId?: string | null;
  runStatus?: string | null;
  runLiveness?: ProcessingLiveness;
  runFailureSummary?: {
    failed_segments: number;
    operation_counts: PromptRunFailureOperationCounts;
    segments: PromptRunFailedSegment[];
  } | null;
  indexId?: string;
  onMarkerChange?: (videoId: string, marker: MarkerInfo) => void;
}


const VideoTile: React.FC<VideoTileProps> = ({
  video,
  onTileClick,
  onProcess,
  onDelete,
  isProcessing,
  isReadOnly = false,
  isInteractive = true,
  isSelected = false,
  selectionMode = false,
  infoMode = 'hover',
  onSelectionChange,
  isActiveInSidebar,
  showMarker = true,
  showMetadataKeys = true,
  showStatusText = true,
  showTopStatusBar = true,
  showSegmentStatusBarsAlways = false,
  showSegmentStatusBars = false,
  runScopeId = null,
  runStatus,
  runLiveness,
  runFailureSummary = null,
  indexId,
  onMarkerChange,
}) => {
  const duration = formatMediaDuration(video.duration_seconds);
  const createdDate = new Date(video.created_at).toLocaleDateString();
  const createdDateLabel = (
    <time className="tile-date" dateTime={video.created_at} title={`Created ${createdDate}`}>
      {createdDate}
    </time>
  );
  const [showActions, setShowActions] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [localMarker, setLocalMarker] = useState(video.marker);
  const previewProjection = useMemo<PreviewGrantProjection | null>(() => {
    const thumbnailGcsUri = typeof video.thumbnail_gcs_uri === 'string'
      ? video.thumbnail_gcs_uri.trim() || null
      : null;
    const gifGcsUri = typeof video.gif_gcs_uri === 'string'
      ? video.gif_gcs_uri.trim() || null
      : null;
    if (!thumbnailGcsUri && !gifGcsUri) return null;
    return {
      identity: { kind: 'video', video_id: video.video_id },
      thumbnailGcsUri,
      gifGcsUri,
    };
  }, [video.gif_gcs_uri, video.thumbnail_gcs_uri, video.video_id]);
  const displayStatus = useMemo(
    () => getDisplayedVideoStatus(video, runScopeId, runStatus),
    [runScopeId, runStatus, video],
  );
  const progress = getVideoProgressSummary(video, runScopeId, runStatus, runLiveness);
  const hasLiveProgress = isInProgressStatus(progress.status);
  useEffect(() => {
    setLocalMarker(video.marker);
  }, [video.marker]);

  useEffect(() => {
    setShowActions(false);
  }, [video.thumbnail_uri, video.video_id]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processed':
        return <CheckCircle size={14} className="status-completed" />;
      case 'processing':
        return <RefreshCw size={14} className="status-processing" />;
      case 'cancelled':
        return <AlertCircle size={14} className="status-cancelled" />;
      case 'failed':
        return <AlertCircle size={14} className="status-failed" />;
      default:
        return <Clock size={14} className="status-pending" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'processed':
        return 'Processed';
      case 'processing':
        return 'Processing';
      case 'cancelled':
        return 'Cancelled';
      case 'failed':
        return 'Failed';
      default:
        if (video.video_uri) {
          return 'Uploaded';
        }
        return 'Pending';
    }
  };


  const handleTileClick = (e: React.MouseEvent) => {
    if (!showActions) {
      onTileClick(video);
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    setShowActions(false);
    action();
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowActions(false);
  };

  const runSegmentRows = useMemo(() => showSegmentStatusBars
    ? getVideoSegmentStatusRows(video.processing_status, runScopeId || getRunScopedVideoProjection(video)?.run_id)
    : [], [runScopeId, showSegmentStatusBars, video]);

  const hasSegmentStatusRows = runSegmentRows.length > 0;
  const isSegmentStatusMode = showSegmentStatusBars && hasSegmentStatusRows;
  const shouldShowSegmentBars = isSegmentStatusMode && (showSegmentStatusBarsAlways || isHovered);
  const hasRunFailureSummary = !!runFailureSummary && runFailureSummary.failed_segments > 0;
  const failedOperationBadges = useMemo(() => {
    if (!runFailureSummary) {
      return [] as Array<{ key: string; label: string; count: number }>;
    }
    const counts = runFailureSummary.operation_counts || {
      field_extraction: 0,
      image_embedding: 0,
      processing: 0,
    };
    const badges = [
      { key: 'field_extraction', label: 'Fx', count: Number(counts.field_extraction || 0) },
      { key: 'image_embedding', label: 'Im', count: Number(counts.image_embedding || 0) },
      { key: 'processing', label: 'Proc', count: Number(counts.processing || 0) },
    ];
    return badges.filter((badge) => badge.count > 0);
  }, [runFailureSummary]);

  const failedSegmentLabels = useMemo(() => {
    if (!hasRunFailureSummary || !runScopeId || !Array.isArray(runFailureSummary?.segments)) {
      return [] as string[];
    }
    const runProjection = (video.processing_status || []).find((item) => item.run_id === runScopeId);
    const runProjectionSegments = runProjection && Array.isArray(runProjection.segments)
      ? runProjection.segments
      : [];
    const orderedSegmentIds: string[] = runProjectionSegments
      .map((segment) => segment.segment_id)
      .filter((segmentId): segmentId is string => typeof segmentId === 'string' && segmentId.length > 0);
    const ordinalBySegmentId = orderedSegmentIds.reduce<Record<string, number>>((acc, segmentId, index) => {
      acc[segmentId] = index + 1;
      return acc;
    }, {});

    const labels = runFailureSummary.segments.map((segment) => {
      const ordinal = ordinalBySegmentId[segment.segment_id];
      if (ordinal) {
        return `S${ordinal}`;
      }
      return segment.segment_id.length > 10
        ? segment.segment_id.slice(0, 10)
        : segment.segment_id;
    });

    return Array.from(new Set(labels));
  }, [hasRunFailureSummary, runFailureSummary?.segments, runScopeId, video.processing_status]);

  return (
    <div
      className={`video-tile ${displayStatus} ${hasLiveProgress ? 'has-live-progress' : ''} ${progress.lastWorkerActivityAt ? 'has-worker-activity' : ''} ${infoMode === 'compact' ? 'compact-info' : ''} ${onSelectionChange ? 'has-selection-control' : ''} ${showTopStatusBar ? '' : 'top-status-bar-hidden'} ${isSegmentStatusMode ? 'segment-status-mode' : ''} ${isSelected ? 'selected' : ''} ${isActiveInSidebar ? 'active-in-sidebar' : ''}`}
      onClick={isInteractive ? handleTileClick : undefined}
      onPointerEnter={handleMouseEnter}
      onPointerLeave={handleMouseLeave}
      onFocus={() => setIsFocusWithin(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsFocusWithin(false);
      }}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? `${selectionMode ? 'Select' : 'Open'} ${video.title}` : undefined}
      aria-pressed={isInteractive ? (selectionMode ? isSelected : isActiveInSidebar) : undefined}
      onKeyDown={isInteractive ? (event) => {
        if (
          event.target === event.currentTarget
          && (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          onTileClick(video);
        }
      } : undefined}
    >
      {/* Selection Checkbox */}
      {onSelectionChange && (
        <div className="tile-selection">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectionChange(video.video_id, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {showMarker && (
        <div className="video-tile-marker" onClick={(e) => e.stopPropagation()}>
          <MarkerEditor
            marker={localMarker}
            target={{
              entity_type: 'video',
              index_id: indexId,
              video_id: video.video_id,
            }}
            onMarkerChange={(next) => {
              setLocalMarker(next);
              onMarkerChange?.(video.video_id, next);
            }}
          />
        </div>
      )}
      
      {/* Canonical video poster */}
      <GrantedPreviewImage
        projection={previewProjection}
        alt={`${video.title} preview`}
        className="preview-image"
        containerClassName="tile-preview"
        engaged={isHovered || isFocusWithin}
        placeholder={(
          <div className="preview-placeholder">
            <Play size={24} />
          </div>
        )}
      >
        {duration !== '-' && (
          <span className="tile-duration" aria-label={`Duration ${duration}`}>{duration}</span>
        )}

        {/* Status Overlay */}
        <div className="status-overlay">
          {getStatusIcon(displayStatus)}
        </div>

        {hasRunFailureSummary && (
          <div className="run-failure-badges" aria-label="Partial failures in this video">
            <span className="partial-failure-label">Partial failures</span>
            <div className="run-failure-op-badge-row">
              {failedOperationBadges.map((badge) => (
                <span key={badge.key} className="run-failure-op-badge">{badge.label} {badge.count}</span>
              ))}
            </div>
            {failedSegmentLabels.length > 0 && (
              <div className="run-failure-segment-labels">
                {failedSegmentLabels.slice(0, 5).join(', ')}
                {failedSegmentLabels.length > 5 ? ` +${failedSegmentLabels.length - 5}` : ''}
              </div>
            )}
          </div>
        )}

        {shouldShowSegmentBars && <VideoSegmentStatusBars rows={runSegmentRows} />}

      </GrantedPreviewImage>

      {hasLiveProgress && <div className="tile-live-progress"><ProcessingProgressBar {...progress} /></div>}

      {/* Video Info */}
      <div className="tile-info">
        <h4 className="tile-title" title={video.title}>
          {video.title}
        </h4>
        {infoMode === 'compact' ? (
          <>
            {showStatusText && (
              <span className={`status-text ${displayStatus}`}>{getStatusText(displayStatus)}</span>
            )}
            {createdDateLabel}
          </>
        ) : (
          <div className="tile-status">
            {showStatusText && !isSegmentStatusMode && (
              <span className={`status-text ${displayStatus}`}>
                {getStatusText(displayStatus)}
              </span>
            )}
            {createdDateLabel}
          </div>
        )}
        <ImageEnrichmentStatus progress={getRunScopedVideoProjection(video, runScopeId)?.enrichment?.image} />

        {/* Metadata Keys */}
        {infoMode !== 'compact' && showMetadataKeys && video.metadata_keys && video.metadata_keys.length > 0 && (
          <div className="tile-metadata-keys">
            {video.metadata_keys.slice(0, 3).map((key, index) => (
              <span key={index} className="metadata-key-tag">
                {key}
              </span>
            ))}
            {video.metadata_keys.length > 3 && (
              <span className="metadata-key-more">+{video.metadata_keys.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* Actions Menu */}
      {!isReadOnly && (
        <div className="tile-actions">
          <button
            className="actions-trigger"
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
          >
            <MoreHorizontal size={16} />
          </button>

          {showActions && (
            <div className="actions-menu">
              {onProcess && displayStatus !== 'processed' && displayStatus !== 'processing' && displayStatus !== 'cancelled' && (
                <button
                  className="action-item process"
                  onClick={(e) => handleActionClick(e, () => onProcess(video.video_id))}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <RefreshCw size={14} className="spinning" />
                  ) : (
                    <Play size={14} />
                  )}
                  {isProcessing ? 'Processing...' : 'Process'}
                </button>
              )}
              <button
                className="action-item delete"
                onClick={(e) => handleActionClick(e, () => onDelete(video.video_id))}
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoTile;
